import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isLocalhost } from 'common';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { createCart, extendSession } from '../domain/api';
import { Path } from '../domain/constants';
import { BookingStatus, useBookingStore } from '../domain/store';
import { getCepDataFromQueryParams, getMmbConfirmationUrl, sendGoogleAnalyticsTransaction } from '../domain/utils';

import { useLocations } from './useLocations';

export function useInitiateBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const store = useBookingStore();
  const { getLocationByIdentifier } = useLocations();
  const hasNavigated = useRef(false);

  const createCartMutation = useMutation({
    mutationFn: createCart,
  });

  const cartId = store.booking?.cartId;
  const mutationCartId = createCartMutation?.data?.id;
  const hasQueryParams = !!location.search;

  useEffect(() => {
    if (!cartId) {
      createCartMutation.mutate();
    }
  }, [cartId]);

  useEffect(() => {
    if (!getLocationByIdentifier) {
      return;
    }

    if (cartId && !hasQueryParams) {
      return;
    }

    // User "navigates" to app with queryparams in the middle of the flow
    if (hasQueryParams && cartId && !hasNavigated.current) {
      store.clear();
      return;
    }

    if (hasNavigated.current || !mutationCartId || !createCartMutation.data) {
      return;
    }

    const cepData = getCepDataFromQueryParams(location);

    store.initiate({
      cartId: createCartMutation.data.id,
      cepData,
      signedInUser: createCartMutation.data.signedInUser,
      getLocationByIdentifier,
    });

    hasNavigated.current = true;
    navigate(Path.FLIGHTS, { replace: true });
  }, [getLocationByIdentifier, cartId, mutationCartId, hasQueryParams]);

  // Reset hasNavigated flag based on location change.
  // removing the query params:
  // > navigate(Path.FLIGHTS, { replace: true })
  // is not completely syncron and reliable
  useEffect(() => {
    if (hasNavigated.current) {
      const timer = setTimeout(() => {
        hasNavigated.current = false;
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [location]);

  if (createCartMutation.isError) {
    throw createCartMutation.error;
  }

  return !!cartId;
}

export function useBookingState() {
  const state = useBookingStore();
  const booking = state?.booking;

  if (!booking) {
    throw new Error('Booking not initiated');
  }

  return booking;
}

export const useRouteValidator = () => {
  const store = useBookingStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const currentPathnameRef = useRef('');
  const status = store.booking?.status;

  const extendSessionMutation = useMutation({
    mutationFn: extendSession,
    onSuccess: ({ sessionId }) => store.setSessionId(sessionId),
    onError: error => {
      throw error;
    },
  });

  // Extend the session when user navigates to a new route so that the
  // user doesn't get logged out in the middle of the booking process.
  useLayoutEffect(() => {
    if (currentPathnameRef.current !== location.pathname) {
      extendSessionMutation.mutate(store.booking?.signedInUser?.sessionId);
      currentPathnameRef.current = location.pathname;
    }
  }, [currentPathnameRef.current, location.pathname]);

  // Clear everything and navigate to MMB when PNR is confirmed
  useLayoutEffect(() => {
    if (!store.booking) {
      return;
    }

    const { pnr, orderId } = store.booking;

    if (status === BookingStatus.SUCCESS && pnr && orderId) {
      const currentState = { ...store.booking };
      const { lastName } = Object.values(currentState.passengers)[0];

      queryClient.clear();
      store.clear();

      if (isLocalhost()) {
        console.log(`>> Booking confirmed with orderid ${orderId}`);
        navigate(Path.FLIGHTS);
      } else {
        sendGoogleAnalyticsTransaction(currentState);
        window.location.href = getMmbConfirmationUrl({ pnr, lastName, orderId });
      }
    }
  }, [status]);
};
